import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { formatError } from '../utils/errorHandler';
import { canClaimOrReturnHandout } from '../utils/planAccess';
import './EndOfDayHandoutsSection.css';

/**
 * Surfaces claimed handouts during end-of-day day-attendance checkout.
 * Blocks proceed until all are returned or the keep-exception is checked.
 */
const EndOfDayHandoutsSection = ({
  active,
  user,
  initialHandouts,
  onBlockingChange,
  onHandoutsChange,
  onError
}) => {
  const { t } = useTranslation('dashboard');
  const [handouts, setHandouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [returningId, setReturningId] = useState(null);
  const [returningAll, setReturningAll] = useState(false);
  const [keepException, setKeepException] = useState(false);
  const canReturn = canClaimOrReturnHandout(user);
  const onBlockingChangeRef = useRef(onBlockingChange);
  const onHandoutsChangeRef = useRef(onHandoutsChange);
  const onErrorRef = useRef(onError);
  const initialHandoutsRef = useRef(initialHandouts);

  onBlockingChangeRef.current = onBlockingChange;
  onHandoutsChangeRef.current = onHandoutsChange;
  onErrorRef.current = onError;
  initialHandoutsRef.current = initialHandouts;

  useEffect(() => {
    if (!active) {
      setHandouts([]);
      setKeepException(false);
      setReturningId(null);
      setReturningAll(false);
      setLoadFailed(false);
      setLoading(false);
      onBlockingChangeRef.current?.(false);
      return undefined;
    }

    let cancelled = false;
    const seed = Array.isArray(initialHandoutsRef.current) ? initialHandoutsRef.current : null;

    const load = async () => {
      if (seed) {
        setHandouts(seed);
        setLoadFailed(false);
        setLoading(false);
        onHandoutsChangeRef.current?.(seed);
        return;
      }

      setLoading(true);
      setLoadFailed(false);
      try {
        const response = await axios.get('/api/handouts/mine');
        if (cancelled) {
          return;
        }
        const next = response.data.data || [];
        setHandouts(next);
        onHandoutsChangeRef.current?.(next);
      } catch (error) {
        if (!cancelled) {
          setLoadFailed(true);
          setHandouts([]);
          onErrorRef.current?.(formatError(error) || t('failedLoadHandouts'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [active, t]);

  useEffect(() => {
    if (!active) {
      return;
    }
    const blocking = loading || loadFailed || (handouts.length > 0 && !keepException);
    onBlockingChangeRef.current?.(blocking);
  }, [active, handouts.length, keepException, loading, loadFailed]);

  const applyHandouts = (next) => {
    setHandouts(next);
    onHandoutsChangeRef.current?.(next);
    if (next.length === 0) {
      setKeepException(false);
    }
  };

  const handleReturnOne = async (handoutId) => {
    if (returningId || returningAll) {
      return;
    }
    setReturningId(handoutId);
    try {
      await axios.post(`/api/handouts/${handoutId}/return`);
      applyHandouts(handouts.filter((handout) => handout.id !== handoutId));
    } catch (error) {
      onErrorRef.current?.(formatError(error) || t('failedReturnHandout'));
    } finally {
      setReturningId(null);
    }
  };

  const handleReturnAll = async () => {
    if (returningId || returningAll || handouts.length === 0) {
      return;
    }
    setReturningAll(true);
    try {
      await axios.post('/api/handouts/return-mine');
      applyHandouts([]);
    } catch (error) {
      onErrorRef.current?.(formatError(error) || t('failedReturnHandout'));
    } finally {
      setReturningAll(false);
    }
  };

  if (!active) {
    return null;
  }

  const busy = returningId != null || returningAll;

  return (
    <section className="eod-handouts" aria-labelledby="eod-handouts-title">
      <h3 id="eod-handouts-title" className="eod-handouts-title">
        {t('eodHandoutsTitle')}
      </h3>
      <p className="eod-handouts-help">{t('eodHandoutsHelp')}</p>

      {loading ? (
        <p className="eod-handouts-status">{t('eodHandoutsLoading')}</p>
      ) : loadFailed ? (
        <p className="eod-handouts-status eod-handouts-status--error">{t('failedLoadHandouts')}</p>
      ) : handouts.length === 0 ? (
        <p className="eod-handouts-status eod-handouts-status--ok">{t('eodHandoutsClear')}</p>
      ) : (
        <>
          <ul className="eod-handouts-list">
            {handouts.map((handout) => (
              <li key={handout.id} className="eod-handouts-item">
                <div className="eod-handouts-item-content">
                  <span className="eod-handouts-name">{handout.name}</span>
                  {handout.location_name && (
                    <span className="eod-handouts-meta">{handout.location_name}</span>
                  )}
                </div>
                {canReturn && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleReturnOne(handout.id)}
                    disabled={busy}
                  >
                    {t('return')}
                  </button>
                )}
              </li>
            ))}
          </ul>

          {canReturn && (
            <button
              type="button"
              className="btn btn-secondary eod-handouts-return-all"
              onClick={handleReturnAll}
              disabled={busy}
            >
              {returningAll ? t('eodReturningAll') : t('eodReturnAll')}
            </button>
          )}

          <label className="eod-handouts-exception">
            <input
              type="checkbox"
              checked={keepException}
              onChange={(event) => setKeepException(event.target.checked)}
            />
            {t('eodKeepHandoutsException')}
          </label>
        </>
      )}
    </section>
  );
};

export default EndOfDayHandoutsSection;
